import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_CARD, BG_SURFACE } from '../components/legacy-ball/table-management/designTokens';

interface Broadcast {
  id: string;
  title: string;
  body: string;
  audience: string;
  active: boolean;
  persistent: boolean;
  expiresAt: string | null;
  createdAt: string;
  creator?: { fullName: string; email: string } | null;
  _count?: { dismissals: number };
}

const AUDIENCES = [
  { value: 'All', label: 'All Users' },
  { value: 'ChapterMembers', label: 'Chapter Members Only' },
  { value: 'Delegates', label: 'Delegates & Above' },
  { value: 'Organizers', label: 'Organizers Only' },
];

export function LoginBroadcastsPage() {
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: '', body: '', audience: 'All', persistent: false, expiresAt: '' });
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ title: '', body: '', audience: 'All', persistent: false, expiresAt: '' });
  const [saving, setSaving] = useState(false);

  const fetchBroadcasts = useCallback(async () => {
    try {
      setError('');
      const data = await apiClient<Broadcast[]>('/login-broadcasts');
      setBroadcasts(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load broadcasts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setCreating(true);
    try {
      await apiClient('/login-broadcasts', {
        method: 'POST',
        body: {
          title: form.title.trim(),
          body: form.body.trim(),
          audience: form.audience,
          persistent: form.persistent,
          expiresAt: form.expiresAt || undefined,
        },
      });
      setForm({ title: '', body: '', audience: 'All', persistent: false, expiresAt: '' });
      setShowCreate(false);
      fetchBroadcasts();
    } catch (err: any) {
      setError(err?.message || 'Failed to create broadcast');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await apiClient(`/login-broadcasts/${id}/toggle`, { method: 'PATCH' });
      setBroadcasts((prev) =>
        prev.map((b) => (b.id === id ? { ...b, active: !b.active } : b)),
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to toggle');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this broadcast? This cannot be undone.')) return;
    try {
      await apiClient(`/login-broadcasts/${id}`, { method: 'DELETE' });
      setBroadcasts((prev) => prev.filter((b) => b.id !== id));
    } catch (err: any) {
      setError(err?.message || 'Failed to delete');
    }
  };

  const handleStartEdit = (b: Broadcast) => {
    setEditingId(b.id);
    setEditForm({
      title: b.title,
      body: b.body,
      audience: b.audience,
      persistent: b.persistent,
      expiresAt: b.expiresAt ? b.expiresAt.slice(0, 16) : '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editForm.title.trim() || !editForm.body.trim()) return;
    setSaving(true);
    try {
      await apiClient(`/login-broadcasts/${editingId}`, {
        method: 'PATCH',
        body: {
          title: editForm.title.trim(),
          body: editForm.body.trim(),
          audience: editForm.audience,
          persistent: editForm.persistent,
          expiresAt: editForm.expiresAt || null,
        },
      });
      setEditingId(null);
      fetchBroadcasts();
    } catch (err: any) {
      setError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.pageTitle}>Login Broadcasts</h1>
          <p style={styles.subtitle}>
            Messages shown to users when they log in. Users must dismiss to proceed.
          </p>
        </div>
        <button style={styles.createBtn} onClick={() => setShowCreate(true)}>
          + New Broadcast
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Create Form */}
      {showCreate && (
        <div style={styles.createCard}>
          <h3 style={styles.createTitle}>Create Broadcast</h3>
          <div style={styles.formGrid}>
            <div>
              <label style={styles.label}>Title</label>
              <input
                style={styles.input}
                type="text"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g. Important: Meeting Point Change"
                maxLength={200}
              />
            </div>
            <div>
              <label style={styles.label}>Audience</label>
              <select
                style={styles.input}
                value={form.audience}
                onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
              >
                {AUDIENCES.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={styles.label}>Message Body</label>
              <textarea
                style={{ ...styles.input, minHeight: '100px', resize: 'vertical' as const }}
                value={form.body}
                onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                placeholder="Write the message that users will see on login..."
              />
            </div>
            <div>
              <label style={styles.label}>Expires At (optional)</label>
              <input
                style={styles.input}
                type="datetime-local"
                value={form.expiresAt}
                onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.4rem' }}>
              <input
                type="checkbox"
                id="persistent-toggle"
                checked={form.persistent}
                onChange={(e) => setForm((f) => ({ ...f, persistent: e.target.checked }))}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <label htmlFor="persistent-toggle" style={{ fontSize: '0.82rem', color: TEXT_DARK, cursor: 'pointer' }}>
                Show every login <span style={{ color: TEXT_MUTED, fontSize: '0.75rem' }}>(message reappears even after dismissal)</span>
              </label>
            </div>
          </div>
          <div style={styles.formActions}>
            <button style={styles.cancelBtn} onClick={() => setShowCreate(false)}>Cancel</button>
            <button
              style={styles.submitBtn}
              onClick={handleCreate}
              disabled={creating || !form.title.trim() || !form.body.trim()}
            >
              {creating ? 'Creating...' : 'Create Broadcast'}
            </button>
          </div>
        </div>
      )}

      {/* Broadcasts List */}
      {loading ? (
        <p style={{ textAlign: 'center', color: TEXT_MUTED, padding: '2rem' }}>Loading...</p>
      ) : broadcasts.length === 0 ? (
        <div style={styles.emptyState}>
          <span style={{ fontSize: '2rem' }}>📢</span>
          <p>No login broadcasts yet. Create one to send a message to users on their next login.</p>
        </div>
      ) : (
        <div style={styles.list}>
          {broadcasts.map((b) => (
            <div key={b.id} style={{ ...styles.card, opacity: b.active ? 1 : 0.6 }}>
              {editingId === b.id ? (
                /* Inline edit form */
                <div>
                  <div style={styles.formGrid}>
                    <div>
                      <label style={styles.label}>Title</label>
                      <input
                        style={styles.input}
                        type="text"
                        value={editForm.title}
                        onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                        maxLength={200}
                      />
                    </div>
                    <div>
                      <label style={styles.label}>Audience</label>
                      <select
                        style={styles.input}
                        value={editForm.audience}
                        onChange={(e) => setEditForm((f) => ({ ...f, audience: e.target.value }))}
                      >
                        {AUDIENCES.map((a) => (
                          <option key={a.value} value={a.value}>{a.label}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={styles.label}>Message Body</label>
                      <textarea
                        style={{ ...styles.input, minHeight: '100px', resize: 'vertical' as const }}
                        value={editForm.body}
                        onChange={(e) => setEditForm((f) => ({ ...f, body: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label style={styles.label}>Expires At (optional)</label>
                      <input
                        style={styles.input}
                        type="datetime-local"
                        value={editForm.expiresAt}
                        onChange={(e) => setEditForm((f) => ({ ...f, expiresAt: e.target.value }))}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingTop: '1.4rem' }}>
                      <input
                        type="checkbox"
                        id={`edit-persistent-${b.id}`}
                        checked={editForm.persistent}
                        onChange={(e) => setEditForm((f) => ({ ...f, persistent: e.target.checked }))}
                        style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                      />
                      <label htmlFor={`edit-persistent-${b.id}`} style={{ fontSize: '0.82rem', color: TEXT_DARK, cursor: 'pointer' }}>
                        Show every login
                      </label>
                    </div>
                  </div>
                  <div style={styles.formActions}>
                    <button style={styles.cancelBtn} onClick={() => setEditingId(null)}>Cancel</button>
                    <button
                      style={styles.submitBtn}
                      onClick={handleSaveEdit}
                      disabled={saving || !editForm.title.trim() || !editForm.body.trim()}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              ) : (
                /* Display mode */
                <>
                  <div style={styles.cardHeader}>
                    <div style={{ flex: 1 }}>
                      <div style={styles.cardTitle}>
                        {b.active ? '🟢' : '⚫'} {b.title}
                      </div>
                      <div style={styles.cardMeta}>
                        {AUDIENCES.find((a) => a.value === b.audience)?.label || b.audience}
                        {b.persistent && <span style={{ marginLeft: '6px', backgroundColor: '#fff3e0', color: '#e65100', padding: '1px 6px', borderRadius: '8px', fontSize: '0.68rem', fontWeight: 600 }}>🔁 Every Login</span>}
                        {' · '}
                        {b._count?.dismissals || 0} dismissed
                        {b.expiresAt && ` · Expires ${new Date(b.expiresAt).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div style={styles.cardActions}>
                      <button
                        style={styles.iconBtn}
                        onClick={() => handleStartEdit(b)}
                        title="Edit"
                        aria-label={`Edit broadcast: ${b.title}`}
                      >
                        ✏️
                      </button>
                      <button
                        style={styles.iconBtn}
                        onClick={() => handleToggle(b.id)}
                        title={b.active ? 'Deactivate' : 'Activate'}
                        aria-label={b.active ? 'Deactivate broadcast' : 'Activate broadcast'}
                      >
                        {b.active ? '⏸️' : '▶️'}
                      </button>
                      <button
                        style={styles.iconBtn}
                        onClick={() => handleDelete(b.id)}
                        title="Delete"
                        aria-label={`Delete broadcast: ${b.title}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div style={styles.cardBody}>{b.body}</div>
                  <div style={styles.cardFooter}>
                    Created {new Date(b.createdAt).toLocaleDateString()}
                    {b.creator && ` by ${b.creator.fullName}`}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    maxWidth: '800px',
    margin: '0 auto',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    flexWrap: 'wrap' as const,
    gap: '1rem',
  },
  pageTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  subtitle: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    margin: '0.25rem 0 0',
  },
  createBtn: {
    padding: '0.6rem 1.2rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  error: {
    backgroundColor: '#fdecea',
    color: '#d32f2f',
    padding: '0.6rem 1rem',
    borderRadius: '6px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  createCard: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1.5rem',
  },
  createTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 1rem',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.8rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    marginBottom: '0.3rem',
  },
  input: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '5px',
    fontSize: '0.85rem',
    boxSizing: 'border-box' as const,
  },
  formActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '1rem',
  },
  cancelBtn: {
    padding: '0.5rem 1rem',
    backgroundColor: '#e0e0e0',
    color: '#333',
    border: 'none',
    borderRadius: '5px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '0.5rem 1.2rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  list: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  card: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1rem',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.5rem',
  },
  cardTitle: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  cardMeta: {
    fontSize: '0.78rem',
    color: TEXT_MUTED,
    marginTop: '0.2rem',
  },
  cardActions: {
    display: 'flex',
    gap: '0.25rem',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px 6px',
    fontSize: '0.9rem',
    borderRadius: '4px',
    lineHeight: 1,
  },
  cardBody: {
    fontSize: '0.85rem',
    color: '#444',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap' as const,
    backgroundColor: BG_SURFACE,
    padding: '0.75rem',
    borderRadius: '5px',
    marginBottom: '0.5rem',
  },
  cardFooter: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
  },
};
